/**
 * CardZoneCalibrator — BETA 22.87 r8, Dylan's tool.
 *
 * "Instead of you pixel peeping and guessing": all 4 card tiers side by side,
 * every text/art zone drawn as its colored box, and every box drag-movable +
 * drag-resizable by hand. SAVE persists the fractions (localStorage override,
 * picked up live by the level-up renderer) and copies the JSON so the final
 * numbers can be baked into CARD_TIER_BOXES in perkCards.ts.
 *
 * Opened from the DevPanel 🃏 button (dev users only).
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import {
  getCardTierBoxes, applyCardCalOverride, clearCardCalOverride,
  type CardTierBoxes, type ZoneRect,
} from '../game/perkCards';

const TIERS = ['common', 'rare', 'epic', 'legendary'] as const;
type Tier = typeof TIERS[number];
type ZoneKey = keyof CardTierBoxes;

const ZONES: { key: ZoneKey; color: string; label: string }[] = [
  { key: 'win',  color: '#00ff66', label: 'WINDOW' },
  { key: 'name', color: '#00e5ff', label: 'NAME' },
  { key: 'lv',   color: '#ffe000', label: 'LV' },
  { key: 'eff',  color: '#ff40ff', label: 'EFFECT' },
  { key: 'cat',  color: '#ff9500', label: 'CATEGORY' },
];

const TIER_LABEL: Record<Tier, string> = {
  common: 'COMMON', rare: 'RARE', epic: 'EPIC', legendary: 'LEGENDARY',
};
const TIER_COLOR: Record<Tier, string> = {
  common: '#e8e8e8', rare: '#b06cff', epic: '#ff4a4a', legendary: '#ffd700',
};

// sample copy so the boxes can be judged against real text, in the real fonts
const SAMPLE = {
  name: 'LUCKY STROKE',
  lv: 'LV 3',
  eff: 'Chance to land critical hits.',
  cat: 'DAMAGE',
};

type Boxes = Record<Tier, CardTierBoxes>;
const snapshot = (): Boxes => JSON.parse(JSON.stringify({
  common: getCardTierBoxes('common'),
  rare: getCardTierBoxes('rare'),
  epic: getCardTierBoxes('epic'),
  legendary: getCardTierBoxes('legendary'),
}));

type DragMode = 'move' | 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';
interface Drag {
  tier: Tier; zone: ZoneKey; mode: DragMode;
  startX: number; startY: number; startRect: ZoneRect;
  cardW: number; cardH: number;
}

export default function CardZoneCalibrator({ onClose }: { onClose: () => void }) {
  const [boxes, setBoxes] = useState<Boxes>(snapshot);
  const [sel, setSel] = useState<{ tier: Tier; zone: ZoneKey }>({ tier: 'common', zone: 'name' });
  const [flash, setFlash] = useState('');
  const dragRef = useRef<Drag | null>(null);
  const boxesRef = useRef(boxes);
  boxesRef.current = boxes;

  // card size: 4 across, 2:3 aspect, fit the viewport
  const [vp, setVp] = useState<[number, number]>([window.innerWidth, window.innerHeight]);
  useEffect(() => {
    const onR = () => setVp([window.innerWidth, window.innerHeight]);
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
  }, []);
  const cardH = Math.min(vp[1] - 170, ((vp[0] - 120) / 4) * 1.5);
  const cardW = cardH * (2 / 3);

  const setRect = useCallback((tier: Tier, zone: ZoneKey, r: ZoneRect) => {
    setBoxes(prev => ({ ...prev, [tier]: { ...prev[tier], [zone]: r } }));
  }, []);

  // ── pointer drag: body = move, handles = resize ──
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      e.preventDefault();
      const fx = (e.clientX - d.startX) / d.cardW;
      const fy = (e.clientY - d.startY) / d.cardH;
      let [x0, y0, x1, y1] = d.startRect;
      if (d.mode === 'move') {
        const w = x1 - x0, h = y1 - y0;
        x0 = Math.min(Math.max(0, x0 + fx), 1 - w); x1 = x0 + w;
        y0 = Math.min(Math.max(0, y0 + fy), 1 - h); y1 = y0 + h;
      } else {
        if (d.mode.includes('w')) x0 = Math.min(Math.max(0, x0 + fx), x1 - 0.02);
        if (d.mode.includes('e')) x1 = Math.max(Math.min(1, x1 + fx), x0 + 0.02);
        if (d.mode.includes('n')) y0 = Math.min(Math.max(0, y0 + fy), y1 - 0.015);
        if (d.mode.includes('s')) y1 = Math.max(Math.min(1, y1 + fy), y0 + 0.015);
      }
      setRect(d.tier, d.zone, [x0, y0, x1, y1]);
    };
    const onUp = () => { dragRef.current = null; };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => { window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp); };
  }, [setRect]);

  // ── arrow keys: nudge selected box (SHIFT = big steps, ALT = resize) ──
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      const step = e.shiftKey ? 0.01 : 0.002;
      const dir: Record<string, [number, number]> = {
        ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step],
      };
      const dv = dir[e.key];
      if (!dv) return;
      e.preventDefault();
      // functional update so held-key repeats never read a stale rect
      setBoxes(prev => {
        const r = [...prev[sel.tier][sel.zone]] as ZoneRect;
        if (e.altKey) {   // resize: move the bottom-right edge
          r[2] = Math.max(Math.min(1, r[2] + dv[0]), r[0] + 0.02);
          r[3] = Math.max(Math.min(1, r[3] + dv[1]), r[1] + 0.015);
        } else {          // move
          r[0] += dv[0]; r[2] += dv[0]; r[1] += dv[1]; r[3] += dv[1];
        }
        return { ...prev, [sel.tier]: { ...prev[sel.tier], [sel.zone]: r } };
      });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sel, onClose, setRect]);

  const doFlash = (msg: string) => { setFlash(msg); setTimeout(() => setFlash(''), 2200); };

  const save = () => {
    const out = JSON.parse(JSON.stringify(boxes));
    applyCardCalOverride(out);
    const json = JSON.stringify({ version: 1, boxes: out }, null, 2);
    console.log('[CARDCAL SAVE]', json);
    try { navigator.clipboard?.writeText(json); } catch { /* clipboard optional */ }
    doFlash('SAVED — live in-game now (JSON copied + logged)');
  };
  const resetAll = () => {
    clearCardCalOverride();
    setBoxes(snapshot());
    doFlash('Reset to shipped defaults');
  };

  const selRect = boxes[sel.tier][sel.zone];
  const selZone = ZONES.find(z => z.key === sel.zone)!;
  const fmt = (n: number) => n.toFixed(3);

  const handleDown = (tier: Tier, zone: ZoneKey, mode: DragMode) => (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSel({ tier, zone });
    dragRef.current = {
      tier, zone, mode, startX: e.clientX, startY: e.clientY,
      startRect: [...boxesRef.current[tier][zone]] as ZoneRect, cardW, cardH,
    };
  };

  const HANDLES: { mode: DragMode; style: React.CSSProperties }[] = [
    { mode: 'nw', style: { left: -5, top: -5, cursor: 'nwse-resize' } },
    { mode: 'ne', style: { right: -5, top: -5, cursor: 'nesw-resize' } },
    { mode: 'sw', style: { left: -5, bottom: -5, cursor: 'nesw-resize' } },
    { mode: 'se', style: { right: -5, bottom: -5, cursor: 'nwse-resize' } },
    { mode: 'n', style: { left: 'calc(50% - 5px)', top: -5, cursor: 'ns-resize' } },
    { mode: 's', style: { left: 'calc(50% - 5px)', bottom: -5, cursor: 'ns-resize' } },
    { mode: 'w', style: { left: -5, top: 'calc(50% - 5px)', cursor: 'ew-resize' } },
    { mode: 'e', style: { right: -5, top: 'calc(50% - 5px)', cursor: 'ew-resize' } },
  ];

  // game-formula font sizes so the sample text matches the real renderer
  const sampleFont = (zone: ZoneKey, r: ZoneRect): React.CSSProperties => {
    const h = r[3] - r[1];
    switch (zone) {
      case 'name': return { fontFamily: 'SorryBossTitle, Anton, Impact, sans-serif', fontSize: Math.min(cardH * 0.068, cardH * h * 0.9), color: '#f3ead9' };
      case 'lv': return { fontFamily: 'SorryBossTitle, Anton, Impact, sans-serif', fontSize: Math.min(cardH * 0.036, cardH * h * 0.86), color: '#ffd700' };
      case 'eff': return { fontFamily: 'SorryBossBody, "Barlow Condensed", sans-serif', fontWeight: 600, fontSize: Math.min(cardH * 0.040, cardH * h * 0.42), color: '#efe9da' };
      case 'cat': return { fontFamily: 'SorryBossTitle, Anton, Impact, sans-serif', fontSize: Math.min(cardH * 0.047, cardH * h * 0.9), color: '#f3ead9' };
      default: return {};
    }
  };

  const btn: React.CSSProperties = {
    padding: '8px 18px', borderRadius: 6, cursor: 'pointer', fontWeight: 700,
    fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 13, letterSpacing: '0.06em',
    border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(40,44,40,0.9)', color: '#eee',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99000, background: 'rgba(8,8,10,0.97)',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      fontFamily: 'ui-monospace, Menlo, monospace', userSelect: 'none', touchAction: 'none',
    }}>
      {/* ── header: title + buttons + selected readout ── */}
      <div style={{ width: '100%', padding: '10px 18px', boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ color: '#fff', fontWeight: 800, fontSize: 15, letterSpacing: '0.08em' }}>🃏 CARD ZONE CALIBRATOR</div>
        <div style={{ color: '#999', fontSize: 11 }}>
          drag box = move · drag edge/corner = resize · arrows = nudge (SHIFT big, ALT resize) · ESC close
        </div>
        <div style={{ flex: 1 }} />
        <button style={{ ...btn, background: '#1f6b2d', borderColor: '#3adf5e' }} onClick={save}>💾 SAVE</button>
        <button style={btn} onClick={resetAll}>RESET ALL</button>
        <button style={btn} onClick={onClose}>CLOSE ✕</button>
      </div>
      <div style={{ height: 22, color: flash ? '#3adf5e' : selZone.color, fontSize: 12, fontWeight: 700 }}>
        {flash || `${TIER_LABEL[sel.tier]} · ${selZone.label} — x ${fmt(selRect[0])}–${fmt(selRect[2])} · y ${fmt(selRect[1])}–${fmt(selRect[3])} · w ${fmt(selRect[2] - selRect[0])} h ${fmt(selRect[3] - selRect[1])}`}
      </div>

      {/* ── the 4 tier cards ── */}
      <div style={{ display: 'flex', gap: 20, marginTop: 8 }}>
        {TIERS.map(tier => (
          <div key={tier}>
            <div style={{ textAlign: 'center', color: TIER_COLOR[tier], fontWeight: 800, fontSize: 13, letterSpacing: '0.12em', marginBottom: 6 }}>
              {TIER_LABEL[tier]}
            </div>
            <div style={{ position: 'relative', width: cardW, height: cardH }}>
              <img
                src={`/perk-cards/${tier}/07_lucky_stroke.webp`}
                draggable={false}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
              />
              {ZONES.map(z => {
                const r = boxes[tier][z.key];
                const isSel = sel.tier === tier && sel.zone === z.key;
                return (
                  <div
                    key={z.key}
                    onPointerDown={handleDown(tier, z.key, 'move')}
                    style={{
                      position: 'absolute',
                      left: `${r[0] * 100}%`, top: `${r[1] * 100}%`,
                      width: `${(r[2] - r[0]) * 100}%`, height: `${(r[3] - r[1]) * 100}%`,
                      border: `2px ${isSel ? 'solid' : 'dashed'} ${z.color}`,
                      background: isSel ? `${z.color}2e` : `${z.color}14`,
                      cursor: 'move', boxSizing: 'border-box',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      zIndex: isSel ? 20 : 10, overflow: 'visible',
                    }}
                  >
                    {z.key !== 'win' && (
                      <span style={{
                        ...sampleFont(z.key, r), pointerEvents: 'none', whiteSpace: 'nowrap',
                        textShadow: '0 2px 4px rgba(0,0,0,0.9)', lineHeight: 1,
                      }}>
                        {SAMPLE[z.key as keyof typeof SAMPLE]}
                      </span>
                    )}
                    {isSel && HANDLES.map(hd => (
                      <div
                        key={hd.mode}
                        onPointerDown={handleDown(tier, z.key, hd.mode)}
                        style={{
                          position: 'absolute', width: 10, height: 10,
                          background: z.color, border: '1px solid #000',
                          borderRadius: 2, zIndex: 30, ...hd.style,
                        }}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* ── zone legend / selector ── */}
      <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
        {ZONES.map(z => (
          <button
            key={z.key}
            onClick={() => setSel(s => ({ ...s, zone: z.key }))}
            style={{
              ...btn, padding: '5px 12px', fontSize: 11,
              borderColor: z.color, color: z.color,
              background: sel.zone === z.key ? `${z.color}26` : 'rgba(20,22,20,0.9)',
            }}
          >
            {z.label}
          </button>
        ))}
      </div>
    </div>
  );
}
